import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CalendarCheck, FileBadge2, Lock, Mail, ShieldCheck, Stethoscope, UsersRound, Eye, EyeOff } from 'lucide-react';
import { Button, InfoTip } from '@dru/ui';
import { useAuth, errorMessage } from '../lib/auth';
import { landingFor } from '../layouts/ConsoleShell';
import { BRAND } from '@dru/types';

/**
 * Testing builds only (VITE_DEMO_LOGINS=true at build time): the seeded
 * accounts, the worker's demo identity, the fixed code, and a link to every
 * surface — so a tester never has to remember any of it. A customer build
 * leaves this unset and none of it is compiled in.
 */
const DEMO = import.meta.env.VITE_DEMO_LOGINS === 'true';
const EMPLOYEE_APP_URL: string = import.meta.env.VITE_EMPLOYEE_APP_URL ?? 'http://127.0.0.1:5175';
const DEMO_PASSWORD = 'Passw0rd!';
const DEMO_CODE = '111111';
/**
 * Grouped the way the product divides itself, not alphabetically: who runs the
 * platform, who staffs the venues that examine people, and who buys the
 * service. A tester picking a login is nearly always asking "whose side am I
 * on", so the side is the heading rather than something to infer from a
 * landing path. The addresses are real mailboxes, so an invitation or a reset
 * sent to one can actually be opened.
 */
const LOCAL_DEMO_GROUPS = [
  {
    title: BRAND.name,
    note: 'The platform’s own staff and its certifying panel.',
    accounts: [
      { email: 'devcoder2323@gmail.com', role: 'Super Admin', lands: '/admin' },
      { email: 'pbind4545@gmail.com', role: 'Operations Admin', lands: '/ops' },
      { email: 'info@pravinexsolutions.in', role: 'Support', lands: '/ops' },
      { email: 'hookmytrade@gmail.com', role: 'Physician (AFIH)', lands: '/physician' },
    ],
  },
  {
    title: 'Company',
    note: 'The employer whose workforce is being examined — Pune and Chennai plants.',
    accounts: [
      { email: 'pravinexsolution@gmail.com', role: 'Company Admin', lands: '/hr' },
      { email: 'devcoder2323+planthr@gmail.com', role: 'Plant HR · Pune', lands: '/hr' },
      { email: 'devcoder2323+accounts.pune@gmail.com', role: 'Plant Accounts · Pune', lands: 'billing screens pending' },
      { email: 'devcoder2323+accounts.chennai@gmail.com', role: 'Plant Accounts · Chennai', lands: 'billing screens pending' },
    ],
  },
  {
    title: 'Lab',
    note: 'The venue that examines the employee and reports on the samples.',
    accounts: [
      { email: 'shivubind568@gmail.com', role: 'Lab Admin', lands: '/partner' },
      { email: 'pearlrealtydxb@gmail.com', role: 'Lab Technician', lands: '/partner' },
      { email: 'devcoder2323+patho@gmail.com', role: 'Pathologist', lands: '/partner' },
    ],
  },
];

/**
 * The testing deployment's accounts.
 *
 * The deployment is seeded with the same accounts as a local database
 * (seed.ts, seed-clinical.ts, populate-tenant.ts, populate-activity.mjs,
 * 25 Sep 2026), plus a Finance login and the Demo Manufacturing staff an
 * earlier seed left there. Every one was checked to sign in with the same
 * password. Grouped by side, as the local list is.
 */
const DEPLOYED_DEMO_GROUPS = [
  {
    title: BRAND.name,
    note: 'The platform’s own staff and its certifying panel.',
    accounts: [
      { email: 'devcoder2323@gmail.com', role: 'Super Admin', lands: '/admin' },
      { email: 'pbind4545@gmail.com', role: 'Operations', lands: '/ops' },
      { email: 'info@pravinexsolutions.in', role: 'Support', lands: '/ops' },
      { email: 'finance@dru.test', role: 'Finance', lands: '/admin/billing' },
      { email: 'hookmytrade@gmail.com', role: 'Physician · Dr. Meera Nair', lands: '/physician' },
      // The seeded cases were routed to this surgeon, so this is the queue with work in it.
      { email: 'doctor@demo.test', role: 'Physician · Dr. Rohan Kulkarni (reviews waiting)', lands: '/physician' },
    ],
  },
  {
    title: 'Company',
    note: 'Demo Manufacturing Pvt Ltd — the employer whose workforce is examined.',
    accounts: [
      { email: 'pravinexsolution@gmail.com', role: 'Company Admin', lands: '/hr' },
      { email: 'devcoder2323+planthr@gmail.com', role: 'Plant HR · Pune', lands: '/hr' },
      { email: 'devcoder2323+planthr.chennai@gmail.com', role: 'Plant HR · Chennai', lands: '/hr' },
      { email: 'devcoder2323+accounts.pune@gmail.com', role: 'Plant Accounts · Pune', lands: '/hr/billing' },
      { email: 'devcoder2323+accounts.chennai@gmail.com', role: 'Plant Accounts · Chennai', lands: '/hr/billing' },
      { email: 'hr.chakan@demo.test', role: 'HR Executive', lands: '/hr' },
      { email: 'ehs@demo.test', role: 'EHS Officer', lands: '/hr' },
    ],
  },
  {
    title: 'Venue',
    note: 'Sahyadri Occupational Health Centre — the venue that examines the employees.',
    accounts: [
      { email: 'shivubind568@gmail.com', role: 'Venue admin', lands: '/partner' },
      { email: 'hospital@demo.test', role: 'Front desk', lands: '/partner' },
      { email: 'tech@demo.test', role: 'Station technician', lands: '/partner' },
      { email: 'pearlrealtydxb@gmail.com', role: 'Lab Technician', lands: '/partner' },
      { email: 'devcoder2323+patho@gmail.com', role: 'Pathologist', lands: '/partner' },
    ],
  },
];

/**
 * Which list to show: decided where the page is opened, not how it was
 * built, so one bundle is right on the live host and on a laptop.
 */
const DEMO_GROUPS = typeof window !== 'undefined' && window.location.hostname.endsWith('pravinexsolutions.in')
  ? DEPLOYED_DEMO_GROUPS
  : LOCAL_DEMO_GROUPS;

/**
 * Signing in, in one or two steps.
 *
 * Two steps rather than one screen that reveals a code field: on an account
 * with a second factor the password does not open a session at all, so there
 * is nothing to hold on to between them except a five-minute challenge that
 * grants nothing. Modelling that as a stage rather than a hidden field keeps
 * the screen honest about what has and has not happened yet.
 *
 * The layout is the one place in this product where the brand is allowed to
 * be loud. Everywhere else the rule is 80% white and neutral, because a
 * console is read all day and a status colour has to be the only colour on
 * it — but nobody reads the sign-in screen for eight hours, and it is the
 * first thing a new customer sees. So the gradient and the accent live here
 * and stop at the console door.
 *
 * The panel is decoration and is marked as such: `aria-hidden`, no focusable
 * content, and nothing in it that is not repeated somewhere a screen reader
 * will reach.
 */
export function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };

  // Remembered on this device only when "Remember me" was ticked — the email,
  // never the password (that is the browser's password manager's job). A
  // first sign-in after setting a password arrives with the email filled in.
  const [email, setEmail] = useState<string>(() => (loc.state as { email?: string } | null)?.email ?? rememberedEmail());
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Off by default, and the cookie the API sets follows it: unticked, the
  // session ends with the browser window. These consoles are read on shared
  // plant and reception machines, so staying signed in is something somebody
  // asks for on their own device rather than the default everybody gets.
  const [rememberMe, setRememberMe] = useState(() => Boolean(rememberedEmail()));
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Everyone lands in their own console, never a screen they cannot read. */
  function land(principal: { permissions: string[] } | null | undefined) {
    const permissions = new Set(principal?.permissions ?? []);
    nav(loc.state?.from ?? landingFor(permissions, principal as never), { replace: true });
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      rememberEmail(rememberMe ? email : null);
      const outcome = await login(email, password, rememberMe);
      if (outcome.kind === 'mfa') {
        setChallenge(outcome.challengeToken);
        setPassword('');
      } else {
        land(outcome.principal);
      }
    } catch (err) {
      // The API returns one error for a wrong password, an unknown account and
      // an inactive one — deliberately, so this screen cannot become a way to
      // discover who has an account. Showing its message verbatim keeps that
      // property instead of second-guessing it.
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const principal = await verifyMfa(challenge!, code, rememberMe);
      land(principal);
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  /** Start over rather than leaving somebody stuck on an expired challenge. */
  function startOver() {
    setChallenge(null);
    setCode('');
    setError(null);
  }

  return (
    <div className="flex min-h-screen bg-white">
      <BrandPanel />

      <main className="flex min-h-screen w-full flex-col lg:w-[42%] lg:min-w-[440px]">
        {/* Below lg the brand panel is gone, so the product still has to
            introduce itself — a bare form on white could be anybody's. */}
        <div className="bg-gradient-to-r from-brand-navy to-brand-magenta px-6 py-5 lg:hidden">
          <img
            src={BRAND.logo.full}
            alt={BRAND.logo.alt}
            width={200}
            height={42}
            className="h-[38px] w-auto object-contain brightness-0 invert"
          />
        </div>

        {/* Top-aligned on a phone, centred once there is room. Vertical
            centring in a tall narrow viewport pushes the heading into the
            middle of the screen and leaves a third of it empty above. */}
        <div className="flex flex-1 items-start justify-center px-6 pb-10 pt-8 sm:px-10 lg:items-center lg:py-10">
          <div className="w-full max-w-[380px] animate-rise-in">
            <header className="mb-7">
              <h1 className="text-h2 text-neutral-900">
                {challenge ? 'Two-factor verification' : 'Staff sign in'}
              </h1>
              <p className="mt-1.5 text-small text-neutral-500">
                {challenge
                  ? 'Your password was accepted. One more step before the session opens.'
                  : 'Use the work email your administrator set your account up with.'}
              </p>
            </header>

            {challenge ? (
              <form onSubmit={submitCode} noValidate>
                <Field
                  label="Authenticator code"
                  icon={<ShieldCheck className="h-4 w-4" />}
                  info="Six digits from your authenticator app — or a recovery code if you have lost the device."
                >
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    // `one-time-code` is what lets a phone offer the code from
                    // the notification; without it people retype it by hand.
                    autoComplete="one-time-code"
                    inputMode="text"
                    required
                    autoFocus
                    placeholder="123456"
                    className={`${CONTROL} font-mono tracking-[0.3em]`}
                  />
                </Field>

                {DEMO && (
                  <p className="mt-2 text-caption text-warning-600">
                    Testing build: the code {DEMO_CODE} is accepted.
                  </p>
                )}

                {/* The box was ticked a screen ago and the session is minted
                    here, so say which kind is about to be opened rather than
                    letting the choice disappear behind the code field. */}
                {rememberMe && (
                  <p className="mt-2 text-caption font-normal text-neutral-500">
                    You will stay signed in on this device.
                  </p>
                )}

                {error && <ErrorNote>{error}</ErrorNote>}

                <Button type="submit" loading={busy} className={`mt-6 w-full ${GRADIENT_BTN}`}>
                  Verify and continue
                </Button>

                <button
                  type="button"
                  onClick={startOver}
                  className="mt-3 min-h-[44px] w-full rounded-btn py-2 text-caption font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800"
                >
                  Start again
                </button>
              </form>
            ) : (
              <>
              <form onSubmit={submitPassword} noValidate>
                <Field label="Work email" icon={<Mail className="h-4 w-4" />}>
                  <input
                    type="email"
                    id="login-email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    autoFocus
                    placeholder="you@company.com"
                    className={CONTROL}
                  />
                </Field>

                <div className="mt-4">
                  <Field label="Password" icon={<Lock className="h-4 w-4" />}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="login-password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      placeholder="Your password"
                      className={`${CONTROL} pr-11`}
                    />
                    {/* Eight dots as a placeholder read as a saved password, and
                        on a phone a password nobody can see is typed twice. */}
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-btn text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </Field>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  {/* 24px box on a phone, where it is tapped with a thumb; the
                      usual 16px once there is a pointer. */}
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-caption font-normal text-neutral-700 sm:min-h-0">
                    <input
                      type="checkbox"
                      name="remember"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-6 w-6 shrink-0 sm:h-4 sm:w-4 cursor-pointer rounded-badge border-neutral-300 accent-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                    />
                    Remember me
                  </label>
                  <Link
                    to="/forgot-password"
                    className="inline-flex min-h-[44px] items-center text-caption font-medium text-primary-700 underline-offset-2 hover:underline sm:min-h-0"
                  >
                    Forgotten your password?
                  </Link>
                </div>

                {error && <ErrorNote>{error}</ErrorNote>}

                <Button type="submit" loading={busy} className={`mt-5 w-full ${GRADIENT_BTN}`}>
                  Sign in
                </Button>

                <p className="mt-4 rounded-input bg-neutral-50 px-3 py-2.5 text-caption font-normal text-neutral-600">
                  New here? Use the link in your invitation email to set your own password.
                  Employees sign in through the employee app, not here.
                </p>
              </form>
              {DEMO && (
                <DemoAccess
                  onPick={(e) => { setEmail(e); setPassword(DEMO_PASSWORD); setError(null); }}
                />
              )}
              </>
            )}
          </div>
        </div>

        <footer className="px-6 pb-6 text-center text-micro font-normal text-neutral-500 sm:px-10">
          © {new Date().getFullYear()} {BRAND.longName}
        </footer>
      </main>
    </div>
  );
}

/**
 * The 40px control, matched to `Input` in the design system so a field here
 * lines up with the same field inside the product.
 *
 * `pl-10` leaves room for the leading icon; the focus ring is the shared one
 * rather than a browser outline, so keyboard focus is visible on every field.
 */
export const CONTROL =
  'h-10 w-full rounded-input border border-neutral-300 bg-white pl-10 pr-3 text-body '
  + 'text-neutral-900 placeholder:text-neutral-500 transition-colors duration-hover '
  + 'hover:border-neutral-400 focus:border-primary-500 focus:outline-none '
  + 'focus:ring-4 focus:ring-primary-500/15';

/**
 * The one gradient button in the product.
 *
 * `tailwind-merge` keeps `bg-gradient-to-r` and drops nothing it needs: the
 * gradient is a background IMAGE, so it paints over `Button`'s own
 * `bg-primary-600` without either fighting the other, and every behaviour the
 * component carries — disabled while loading, `aria-busy`, the focus ring —
 * survives untouched. Forking the button to restyle it would have lost all of
 * that for a colour.
 */
export const GRADIENT_BTN =
  // A solid navy under the gradient: what shows if the image does not paint
  // (forced colours, a print), and what contrast is measured against.
  '[background-color:theme(colors.brand.navy)] bg-gradient-to-r from-brand-navy to-brand-magenta text-paper shadow-card '
  + 'hover:brightness-[1.07] hover:shadow-dropdown active:brightness-95 '
  // Disabled keeps the gradient at reduced strength: flat grey read as a
  // different, secondary button, with no sign it would light up once the form
  // was filled in.
  + 'disabled:opacity-50 disabled:shadow-none disabled:hover:brightness-100';

/**
 * A link that looks like the primary button.
 *
 * For "Go to sign in" and "Request a new link": they navigate, so they are
 * links. Wrapping a `<Button>` in a `<Link>` put a button inside an anchor —
 * two nested interactive elements, which a screen reader announces twice and
 * a keyboard tabs through twice.
 */
export const LINK_BTN =
  'inline-flex h-10 w-full items-center justify-center rounded-btn px-4 text-body font-semibold '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 '
  + GRADIENT_BTN;

/** "Back to sign in" and friends: a quiet link with a thumb-sized hit area. */
export const QUIET_LINK =
  'inline-flex min-h-[44px] items-center px-2 text-caption font-medium text-primary-700 underline-offset-2 hover:underline';

export function Field({ label, icon, info, children }: {
  label: string;
  icon: ReactNode;
  /** An ⓘ beside the label, drawn outside the <label> so the label still names the input. */
  info?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      {info && (
        <span className="absolute -top-0.5 right-0 max-md:-top-2">
          <InfoTip label={`About ${label}`}>{info}</InfoTip>
        </span>
      )}
      <label className="block">
        <span className="text-caption text-neutral-700">{label}</span>
        <span className="relative mt-1.5 block">
          {/* Decorative: the label already names the field, so announcing the
              icon as well would read the same thing twice. */}
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          >
            {icon}
          </span>
          {children}
        </span>
      </label>
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="mt-4 rounded-input border border-danger-600/25 bg-danger-100 px-3 py-2 text-small text-danger-600"
    >
      {children}
    </div>
  );
}

/**
 * The brand half.
 *
 * Hidden below `lg` rather than stacked: on a phone it would push the form
 * below the fold, and somebody signing in wants the form. What survives on
 * small screens is the logo band above the card.
 *
 * Everything in here is `aria-hidden`. It contains no information a person
 * needs in order to sign in — the same four capabilities are what the product
 * does, not what this page asks of anyone — so announcing a decorative column
 * before the form would put four paragraphs between a screen-reader user and
 * the email field.
 */
export function BrandPanel() {
  return (
    <aside
      aria-hidden
      className="relative hidden overflow-hidden lg:flex lg:w-[58%] lg:flex-col lg:justify-between
                 bg-gradient-to-br from-brand-navy-deep via-brand-navy to-brand-magenta
                 bg-[length:200%_200%] motion-safe:animate-gradient-drift"
    >
      {/* Two soft highlights so the gradient reads as depth rather than as a
          flat sheet of colour. Pure CSS — no image to load and nothing to go
          missing behind a corporate proxy. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.16), transparent 42%),'
            + 'radial-gradient(circle at 82% 78%, rgba(255,255,255,0.10), transparent 45%)',
        }}
      />

      <div className="relative px-12 pt-12">
        <img
          src={BRAND.logo.full}
          alt=""
          width={260}
          height={54}
          // The lockup is dark artwork on a light ground; on the gradient it
          // has to become white, and inverting it is cheaper and sharper than
          // shipping a second file that can drift out of step with the first.
          className="h-[52px] w-auto object-contain brightness-0 invert"
        />
      </div>

      <div className="relative px-12 pb-4">
        <p className="max-w-lg text-[34px] font-bold leading-[1.15] text-paper">
          Statutory occupational health,
          <br />
          from examination to certificate.
        </p>
        <p className="mt-4 max-w-md text-body-lg font-normal text-paper/75">
          One record of every employee’s fitness to work — raised, examined, signed by a
          certifying surgeon, and verifiable at the gate.
        </p>

        <ul className="mt-9 grid max-w-lg grid-cols-2 gap-x-6 gap-y-4">
          <Capability icon={<UsersRound className="h-[18px] w-[18px]" />}>
            Workforce health records
          </Capability>
          <Capability icon={<Stethoscope className="h-[18px] w-[18px]" />}>
            Protocol-driven examinations
          </Capability>
          <Capability icon={<CalendarCheck className="h-[18px] w-[18px]" />}>
            Partner scheduling &amp; capacity
          </Capability>
          <Capability icon={<FileBadge2 className="h-[18px] w-[18px]" />}>
            QR-verifiable certificates
          </Capability>
        </ul>
      </div>

      <div className="relative px-12 pb-12">
        <p className="text-caption font-normal text-paper/60">
          Factories Act 1948 · AFIH certifying surgeon · every company's records kept apart
        </p>
      </div>
    </aside>
  );
}

function Capability({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-btn bg-paper/15 text-paper">
        {icon}
      </span>
      <span className="pt-1 text-small text-paper/90">{children}</span>
    </li>
  );
}

/** The testing-build panel under the sign-in form. See `DEMO` at the top. */
function DemoAccess({ onPick }: { onPick: (email: string) => void }) {
  const links = [
    { label: 'Employee app (PWA)', href: EMPLOYEE_APP_URL, external: true },
    { label: 'Verify a certificate', href: '/verify/', external: true },
    { label: 'API docs (Swagger)', href: '/api/docs', external: true },
    { label: 'Platform console', href: '/admin' },
    { label: 'HR console', href: '/hr' },
    { label: 'Hospital console', href: '/hospital' },
    { label: 'Laboratory console', href: '/lab' },
    { label: 'Physician console', href: '/physician' },
  ];
  return (
    <section
      aria-label="Testing shortcuts"
      className="mt-6 rounded-card border border-warning-600/30 bg-warning-100 p-4 text-caption text-neutral-700"
    >
      <p className="font-semibold text-neutral-900">Testing build — demo access</p>
      <p className="mt-1 text-neutral-600">
        Click an account to fill the form. Password for all:{' '}
        <code className="font-mono">{DEMO_PASSWORD}</code>
      </p>
      {DEMO_GROUPS.map((g) => (
        <div key={g.title} className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-900">{g.title}</p>
          <p className="text-[11px] text-neutral-500">{g.note}</p>
          <ul className="mt-1.5 grid grid-cols-1 gap-1 sm:grid-cols-2">
            {g.accounts.map((a) => (
              <li key={a.email}>
                <button
                  type="button"
                  onClick={() => onPick(a.email)}
                  className="w-full rounded-input border border-neutral-200 bg-white px-2 py-1.5 text-left transition-colors duration-hover hover:border-warning-600"
                >
                  <span className="block truncate font-mono text-[12px] text-neutral-900">{a.email}</span>
                  <span className="block text-[11px] text-neutral-500">{a.role} · {a.lands}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="mt-3">
        Employee app: employee code <code className="font-mono">DEMO-0001</code>, mobile{' '}
        <code className="font-mono">9876500001</code>, OTP <code className="font-mono">{DEMO_CODE}</code>.
        Second factor, when asked: <code className="font-mono">{DEMO_CODE}</code>.
      </p>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {links.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              {...(l.external ? { target: '_blank', rel: 'noreferrer' } : {})}
              className="font-medium text-primary-700 underline-offset-2 hover:underline"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

const REMEMBERED_EMAIL = 'dru.login.email';

/** The email a "Remember me" sign-in left on this device, if any. */
function rememberedEmail(): string {
  try { return localStorage.getItem(REMEMBERED_EMAIL) ?? ''; } catch { return ''; }
}

function rememberEmail(email: string | null) {
  try {
    if (email) localStorage.setItem(REMEMBERED_EMAIL, email.trim());
    else localStorage.removeItem(REMEMBERED_EMAIL);
  } catch { /* private window: nothing to remember */ }
}
